import {useParams} from "react-router-dom";
import React, {useState, useEffect} from "react";
import {postWithToken, fetchWithToken} from "../../api/apiBase";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogTitle,
    DialogTrigger
} from "../ui/dialog";
import {Button} from "../ui/button";
import {Input} from "../ui/input";
import {Textarea} from "../ui/textarea";
import {useAuthInfo, WithLoggedInAuthInfoProps} from "@propelauth/react";
import {Checkbox} from "../ui/checkbox";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "../ui/tooltip";
import {HelpCircle} from "lucide-react";
import {CopyQlinkButton} from "../qlink/QlinkCopyButton";

interface InviteProps {
    onInvite?: (email: string) => void;
    scopes?: string[];
    // for pages without a scope in the url (eg the bid tab)
    scope_id?: string;
    // pass open to control it from outside (eg a menu) - then there is no button
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

export default function InviteToQuickLink({scopes, onInvite, scope_id: scopeIdProp, open, onOpenChange}: InviteProps) {
    const {project_id, scope_id: scopeIdParam} = useParams<string>();
    const scope_id = scopeIdProp ?? scopeIdParam;
    const {accessToken} = useAuthInfo();
    const [selfOpen, setSelfOpen] = useState<boolean>(false);

    const [companyName, setCompanyName] = useState<string>('');
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [companyMatches, setCompanyMatches] = useState<any[]>([]);
    const [emails, setEmails] = useState<string>('');
    const [message, setMessage] = useState<string>('');
    const [isInviting, setIsInviting] = useState<boolean>(false);
    const [result, setResult] = useState<{message?: string; link?: string; error?: string}>({});
    const [emailLookups, setEmailLookups] = useState<{email: string, found: boolean, company_id?: string, company_name?: string}[]>([]);
    const [emailConflict, setEmailConflict] = useState<string>('');
    const [companyFromEmail, setCompanyFromEmail] = useState<boolean>(false);
    const [requirePWlogin, setRequirePWLogin] = useState<boolean>(true);

    const isValid =  emails.trim() !== '' && !emailConflict;

    // Company name lookup (same pattern as CompanySetup.tsx)
    useEffect(() => {
        if (companyName.length >= 3 && companyId === null) {
            fetchWithToken(`/company/check_org/${companyName}/builder`, accessToken as string)
                .then((res) => {
                    if (res.status_code === 'NAME_MATCH') {
                        setCompanyMatches(res.items || []);
                    } else {
                        setCompanyMatches([]);
                    }
                })
                .catch(() => setCompanyMatches([]));
        } else if (companyName.length < 3) {
            setCompanyMatches([]);
        }
    }, [companyName]);

    // Look up each email to see if they already belong to a company
    useEffect(() => {
        const emailList = emails.split(',').map(e => e.trim()).filter(e => e && e.includes('@'));
        if (emailList.length === 0) {
            setEmailLookups([]);
            setEmailConflict('');
            if (companyFromEmail) {
                setCompanyName('');
                setCompanyId(null);
                setCompanyFromEmail(false);
            }
            return;
        }

        const timer = setTimeout(async () => {
            const results: typeof emailLookups = [];
            for (const email of emailList) {
                try {
                    const res = await fetchWithToken(`/qlink/lookup-email/${email}`, accessToken as string);
                    results.push({email, ...res});
                } catch {
                    results.push({email, found: false});
                }
            }
            setEmailLookups(results);

            // Check for conflicts: multiple different companies
            const companies = results.filter(r => r.found && r.company_id);
            const uniqueCompanyIds = Array.from(new Set(companies.map(r => r.company_id)));

            if (uniqueCompanyIds.length > 1) {
                setEmailConflict('These emails belong to different companies');
            } else {
                setEmailConflict('');
            }

            // Auto-fill company from email lookup
            if (uniqueCompanyIds.length === 1 && companies[0].company_name) {
                setCompanyName(companies[0].company_name);
                setCompanyId(companies[0].company_id!);
                setCompanyFromEmail(true);
                setCompanyMatches([]);
            } else if (companies.length === 0 && companyFromEmail) {
                // All emails are new — clear auto-fill
                setCompanyName('');
                setCompanyId(null);
                setCompanyFromEmail(false);
            }
        }, 500); // debounce 500ms

        return () => clearTimeout(timer);
    }, [emails]);

    const handleCompanySelect = (company: any) => {
        setCompanyName(company.name);
        setCompanyId(company.id);
        setCompanyMatches([]);
    };

    const handleCompanyNameChange = (value: string) => {
        setCompanyName(value);
        setCompanyId(null);
    };

    const SendInvite = (e: any) => {
        e.preventDefault();
        setIsInviting(true);
        setResult({});


        const emailList = emails.split(',').map(e => e.trim()).filter(e => e);
        const data = {
            company_name: companyName,
            company_id: companyId,
            emails: emailList,
            scope_id: scope_id || null,
            message: message || null,
            project_id: project_id,
            require_pw: requirePWlogin
        };
        // if (requirePWlogin){
        //     postWithToken(`/projects/${project_id}/invite_user` ,
        //     JSON.stringify(data),
        //     accessToken!)
        //     //  InviteBidderUser(project_id as string, searchText, accessToken)
        //     .then((data) => {
        //         console.log(data)
        //         setResult({message: data.message, link: data.link});
        //         setIsInviting(false)
        //         setMessage(data.message)
        //         if(onInvite){
        //             onInvite(searchText)
        //         }
        //
        //     })
        //     .catch((err) => {
        //         console.log('error', err)
        //         setResult({error: "Could not create invite"})
        //         setIsInviting(false)
        //     })
        // }else {
        postWithToken(`/qlink/${project_id}/invite`,
                JSON.stringify(data),
                accessToken!)
                .then((data) => {
                    setResult({message: data.message, link: data.link});
                    setIsInviting(false);
                    if(onInvite){
                        onInvite(emails)
                    }
                })
                .catch((err) => {
                    setResult({error: "Could not create invite"});
                    setIsInviting(false);
                });
        // }
    };

    const onDialogOpenChange = (o: boolean) => {
        setSelfOpen(o);
        onOpenChange?.(o);
        setCompanyName('');
        setCompanyId(null);
        setCompanyMatches([]);
        setEmails('');
        setMessage('');
        setResult({});
        setEmailLookups([]);
        setEmailConflict('');
        setCompanyFromEmail(false);
    };

    return <Dialog open={open ?? selfOpen} onOpenChange={onDialogOpenChange}>
        {open === undefined &&
            <DialogTrigger asChild>
                <Button>Invite Bidders</Button>
            </DialogTrigger>
        }
        <DialogContent className="sm:max-w-md">
            <DialogTitle className="font-normal">Invite to Bid</DialogTitle>
            <div className="space-y-3">
                <div>
                    <label className="text-sm font-medium flex items-center gap-2">
                        <Checkbox
                         checked={requirePWlogin}
                         onCheckedChange={(e) => setRequirePWLogin(e as boolean)}
                        />
                        Require Password
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <HelpCircle className="h-3.5 w-3.5 text-gray-400 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent side="right" className="max-w-[240px] text-xs">
                                    When checked, the user must create an account with a password to access the bid.
                                    When unchecked, they receive a magic link for instant one-click access (no signup required).
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </label>
                </div>
                <div>

                    <label className="text-sm font-medium">Email(s)</label>
                    <Input
                        placeholder="john@acme.com, jane@acme.com"
                        value={emails}
                        onChange={(e) => setEmails(e.target.value)}
                    />
                    <p className="text-xs text-gray-500 mt-1">Comma-separated</p>
                    {emailConflict && <p className="text-xs text-red-600 mt-1">{emailConflict}</p>}
                    {emailLookups.length > 0 && !emailConflict && (
                        <div className="mt-1 space-y-0.5">
                            {emailLookups.map((r) => (
                                <p key={r.email} className="text-xs text-gray-500">
                                    {r.email} — {r.found ? <span className="text-green-600">{r.company_name}</span> : 'new user'}
                                </p>
                            ))}
                        </div>
                    )}
                </div>
                <div>
                    <label className="text-sm font-medium">Company Name</label>
                    <Input
                        placeholder="e.g. Acme Construction"
                        value={companyName}
                        onChange={(e) => handleCompanyNameChange(e.target.value)}
                        disabled={companyFromEmail}
                    />
                    {companyId && <p className="text-xs text-green-600 mt-1">Matched existing company</p>}
                    {companyMatches.length > 0 && (
                        <div className="mt-2 border rounded p-2 space-y-1">
                            <p className="text-xs text-gray-500">Existing companies:</p>
                            {companyMatches.map((item) => (
                                <div key={item.id} className="flex justify-between items-center">
                                    <span className="text-sm">{item.name}</span>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleCompanySelect(item)}>
                                        Select
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div>
                    <label className="text-sm font-medium">Message (optional)</label>
                    <Textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                    />
                </div>
            </div>
            <Button onClick={SendInvite} disabled={!isValid || isInviting}>
                {isInviting ? 'Sending...' : 'Send Invite'}
            </Button>
            <DialogFooter className="sm:justify-start">
                 <div className="space-y-2 w-full">
                    {result.message && <p className="text-green-600">{result.message}</p>}
                    {result.link && <p className="text-xs text-gray-500">
                        <CopyQlinkButton link_id={result.link}/>
                    </p>}
                    {result.error && <p className="text-red-600">{result.error}</p>}
                 </div>
            </DialogFooter>
        </DialogContent>
    </Dialog>;
}
